import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AppLayout } from './components/layout';
import { ProtectedRoute, SysadminRoute } from './components/auth';
import {
  Login,
  Signup,
  ForgotPassword,
  ResetPassword,
  AcceptInvite,
  Dashboard,
  Insights,
  Orders,
  OrderForm,
  OrderDetails,
  Patients,
  PatientDetail,
  Users,
  Medications,
  MedicationForm,
  EmailTemplates,
  Coupons,
  Settings,
  Configurations,
  Documentation,
  Unauthorized,
} from './pages';
import { UsersRoles, AdminEmailTemplates, Questionnaires, AdminMedications, AdminPharmacies } from './pages/admin';

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* Insights */}
          <Route path="insights" element={<Insights />} />

          {/* Orders */}
          <Route path="orders" element={<Orders />} />
          <Route path="orders/new" element={<OrderForm />} />
          <Route path="orders/:id" element={<OrderDetails />} />
          <Route path="orders/:id/edit" element={<OrderForm />} />
          
          {/* Patients */}
          <Route path="patients" element={<Patients />} />
          <Route path="patients/:id" element={<PatientDetail />} />
          
          {/* Users */}
          <Route path="users" element={<Users />} />
          
          {/* Medications */}
          <Route path="medications" element={<Medications />} />
          <Route path="medications/new" element={<MedicationForm />} />
          <Route path="medications/:id/edit" element={<MedicationForm />} />
          
          {/* Email Templates */}
          <Route path="email-templates" element={<EmailTemplates />} />

          {/* Coupons */}
          <Route path="coupons" element={<Coupons />} />

          {/* Configurations */}
          <Route path="configurations" element={<Configurations />} />
          
          {/* Settings */}
          <Route path="settings" element={<Settings />} />

          {/* Documentation */}
          <Route path="documentation" element={<Documentation />} />

          {/* Sysadmin Routes */}
          <Route path="admin/users-roles" element={<SysadminRoute><UsersRoles /></SysadminRoute>} />
          <Route path="admin/email-templates" element={<SysadminRoute><AdminEmailTemplates /></SysadminRoute>} />
          <Route path="admin/questionnaires" element={<SysadminRoute><Questionnaires /></SysadminRoute>} />
          <Route path="admin/medications" element={<SysadminRoute><AdminMedications /></SysadminRoute>} />
          <Route path="admin/pharmacies" element={<SysadminRoute><AdminPharmacies /></SysadminRoute>} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="!bg-white !shadow-elevated !rounded-xl !border !border-secondary-100"
        className="!text-secondary-700 !text-sm"
      />
    </BrowserRouter>
  );
};

export default App;
